import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { useSubscription } from "@/context/SubscriptionContext";
import { useSchool } from "@/context/SchoolContext";
import { createCreditCheckoutSession } from "@/utils/createCheckoutSession";
import { PageHeader } from "@/layouts/PageHeader";
import { PageContainer } from "@/layouts/PageContainer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Sparkles } from "lucide-react";

const REPORT_PACKS = [
  { id: "reports_5",   label: "5 Credits",   amount: 5 },
  { id: "reports_30",  label: "30 Credits",  amount: 30 },
  { id: "reports_60",  label: "60 Credits",  amount: 60 },
  { id: "reports_120", label: "120 Credits", amount: 120 },
];

const AI_PACKS = [
  { id: "ai_25",  label: "25 Credits",  amount: 25 },
  { id: "ai_100", label: "100 Credits", amount: 100 },
  { id: "ai_200", label: "200 Credits", amount: 200 },
  { id: "ai_500", label: "500 Credits", amount: 500 },
];

function CreditPackCard({ pack, onBuy, loading }) {
  return (
    <div className="border border-border rounded-xl p-4 bg-card flex flex-col gap-3 hover:border-primary/50 transition-colors">
      <p className="text-2xl font-semibold text-foreground">{pack.amount}</p>
      <p className="text-sm text-muted-foreground">credits</p>
      <Button
        size="sm"
        variant="outline"
        className="w-full mt-auto"
        disabled={loading === pack.id}
        onClick={() => onBuy(pack.id)}
      >
        {loading === pack.id ? "Redirecting..." : "Buy"}
      </Button>
    </div>
  );
}

export default function Billing() {
  const { getToken } = useAuth();
  const { reportsRemaining, aiCreditsRemaining, seatInfo, loading } = useSubscription();
  const { memberships } = useSchool();
  const [buyingPack, setBuyingPack] = useState(null);

  const handleBuy = async (packId) => {
    setBuyingPack(packId);
    try {
      const token = await getToken();
      await createCreditCheckoutSession(packId, token);
    } finally {
      setBuyingPack(null);
    }
  };

  const statusVariant =
    seatInfo?.status === "active"
      ? "success"
      : seatInfo?.status === "past_due"
      ? "warning"
      : "default";

  const statusLabel =
    seatInfo?.status === "active"
      ? "Active"
      : seatInfo?.status === "past_due"
      ? "Past Due"
      : seatInfo?.status === "cancelled"
      ? "Cancelled"
      : seatInfo?.status ?? "Unknown";

  return (
    <PageContainer>
      <PageHeader
        title="Billing"
        subtitle="Buy credits and manage your billing."
      />

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="space-y-8">

          {/* Wallet section */}
          <div>
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
              Your Wallet
            </h2>
            <div className="grid grid-cols-2 gap-4 max-[480px]:grid-cols-1">
              <div className="border border-border rounded-xl p-5 bg-card flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <FileText size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {reportsRemaining ?? "-"}
                  </p>
                  <p className="text-sm text-muted-foreground">Report Credits</p>
                </div>
              </div>
              <div className="border border-border rounded-xl p-5 bg-card flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Sparkles size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {aiCreditsRemaining ?? "-"}
                  </p>
                  <p className="text-sm text-muted-foreground">AI Credits</p>
                </div>
              </div>
            </div>
          </div>

          {/* Buy Credits section */}
          <div>
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
              Buy Credits
            </h2>

            {/* Report Credits */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <FileText size={14} /> Report Credits
              </h3>
              <div className="grid grid-cols-4 gap-3 max-[640px]:grid-cols-2">
                {REPORT_PACKS.map((pack) => (
                  <CreditPackCard
                    key={pack.id}
                    pack={pack}
                    onBuy={handleBuy}
                    loading={buyingPack}
                  />
                ))}
              </div>
            </div>

            {/* AI Credits */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Sparkles size={14} /> AI Credits
              </h3>
              <div className="grid grid-cols-4 gap-3 max-[640px]:grid-cols-2">
                {AI_PACKS.map((pack) => (
                  <CreditPackCard
                    key={pack.id}
                    pack={pack}
                    onBuy={handleBuy}
                    loading={buyingPack}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* School Seat section */}
          {seatInfo !== null && (
            <div>
              <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
                School Seat
              </h2>
              <div className="border border-border rounded-xl p-5 bg-card space-y-4">
                <div className="flex items-center gap-2">
                  <Badge variant={statusVariant}>{statusLabel}</Badge>
                  {seatInfo.periodEnd && (
                    <span className="text-xs text-muted-foreground">
                      Renews {new Date(seatInfo.periodEnd).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 max-[480px]:grid-cols-1">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Reports Used</p>
                    <p className="text-lg font-semibold text-foreground">
                      {seatInfo.reportsUsed} <span className="text-sm font-normal text-muted-foreground">/ 120</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">AI Gens Used</p>
                    <p className="text-lg font-semibold text-foreground">
                      {seatInfo.aiGensUsed} <span className="text-sm font-normal text-muted-foreground">/ 200</span>
                    </p>
                  </div>
                </div>
                {memberships.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Managed by your school administrator. Visit{" "}
                    <Link to="/schools/seats" className="underline hover:text-foreground">
                      Seat Management
                    </Link>{" "}
                    for billing details.
                  </p>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </PageContainer>
  );
}
